import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  TableContainer,
  Stack,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface GeofenceAnalyticsPanelProps {
  analytics: any;
  onRefresh: () => void;
}

const GeofenceAnalyticsPanel: React.FC<GeofenceAnalyticsPanelProps> = ({
  analytics,
  onRefresh
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: isMobile ? 2 : 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
          Geofence Analytics
        </Typography>
        <IconButton onClick={onRefresh} size={isMobile ? 'small' : 'medium'}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Overview Cards - 2x2 on mobile */}
      <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: isMobile ? 2 : 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="caption" color="textSecondary" display="block">
                {isMobile ? 'Zones' : 'Total Zones'}
              </Typography>
              <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
                {analytics.overview.totalZones}
              </Typography>
              <Typography variant="caption">
                {analytics.overview.activeZones} active
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="caption" color="textSecondary" display="block">
                Violations
              </Typography>
              <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" color="error">
                {analytics.overview.totalViolations}
              </Typography>
              <Typography variant="caption">
                Avg {analytics.overview.averageViolationsPerZone} per zone
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="caption" color="textSecondary" display="block">
                Top Violator
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} fontWeight="bold">
                {analytics.topViolators[0]?.plateNumber || 'None'}
              </Typography>
              <Typography variant="caption">
                {analytics.topViolators[0]?.violations || 0} violations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
              <Typography variant="caption" color="textSecondary" display="block">
                Best Zone
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} fontWeight="bold">
                {analytics.zonePerformance[analytics.zonePerformance.length - 1]?.zoneName || 'N/A'}
              </Typography>
              <Typography variant="caption" color="success.main">
                Lowest violations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: isMobile ? 2 : 3 }}>
        {/* Violation Trends */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom fontWeight="bold">
                Violation Trends
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <LineChart data={analytics.violationTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    interval={isMobile ? 'preserveStartEnd' : 0}
                  />
                  <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <Tooltip />
                  {!isMobile && <Legend />}
                  <Line 
                    type="monotone" 
                    dataKey="violations" 
                    stroke="#8884d8" 
                    activeDot={{ r: isMobile ? 6 : 8 }} 
                    strokeWidth={isMobile ? 2 : 3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Violators */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom fontWeight="bold">
                Top 5 Violators
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart data={analytics.topViolators.slice(0, 5)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="plateNumber" 
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    angle={isMobile ? -45 : 0}
                    textAnchor={isMobile ? 'end' : 'middle'}
                    height={isMobile ? 60 : 30}
                  />
                  <YAxis tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <Tooltip />
                  {!isMobile && <Legend />}
                  <Bar dataKey="violations" fill="#ff6b6b" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Zone Performance Table */}
      <Card>
        <CardContent sx={{ p: isMobile ? 1 : 2 }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom fontWeight="bold">
            Zone Performance
          </Typography>
          
          {isMobile ? (
            // Mobile: Card View
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {analytics.zonePerformance.map((zone: any) => (
                <Card key={zone.zoneName} variant="outlined">
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      {zone.zoneName}
                    </Typography>
                    
                    <Stack spacing={0.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="textSecondary">Type:</Typography>
                        <Chip size="small" label={zone.zoneType} sx={{ height: 20, fontSize: '0.7rem' }} />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="textSecondary">Violations:</Typography>
                        <Chip 
                          size="small" 
                          label={zone.totalViolations} 
                          color={zone.totalViolations > 10 ? 'error' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="textSecondary">Speed:</Typography>
                        <Typography variant="caption">{zone.speedViolations}</Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="textSecondary">Vehicles:</Typography>
                        <Typography variant="caption">{zone.affectedVehicles}</Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="textSecondary">Efficiency:</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={zone.efficiency} 
                            sx={{ 
                              flex: 1,
                              height: 6, 
                              borderRadius: 3,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: zone.efficiency > 80 ? 'success.main' : 
                                         zone.efficiency > 60 ? 'warning.main' : 'error.main'
                              }
                            }}
                          />
                          <Typography variant="caption" fontWeight="bold">
                            {Math.round(zone.efficiency)}%
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            // Desktop: Table View
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Zone Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Total Violations</TableCell>
                    <TableCell align="right">Speed Violations</TableCell>
                    <TableCell align="right">Affected Vehicles</TableCell>
                    <TableCell align="right">Efficiency Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.zonePerformance.map((zone: any) => (
                    <TableRow key={zone.zoneName}>
                      <TableCell>{zone.zoneName}</TableCell>
                      <TableCell>
                        <Chip size="small" label={zone.zoneType} />
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          size="small" 
                          label={zone.totalViolations} 
                          color={zone.totalViolations > 10 ? 'error' : 'default'} 
                        />
                      </TableCell>
                      <TableCell align="right">{zone.speedViolations}</TableCell>
                      <TableCell align="right">{zone.affectedVehicles}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={zone.efficiency} 
                            sx={{ 
                              width: 100, 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: zone.efficiency > 80 ? 'success.main' : 
                                         zone.efficiency > 60 ? 'warning.main' : 'error.main'
                              }
                            }}
                          />
                          <Typography variant="body2">
                            {Math.round(zone.efficiency)}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default GeofenceAnalyticsPanel;